import React, { useState, useEffect } from 'react';
import { Container, Form, InputGroup, Button, Spinner, Badge, Modal } from 'react-bootstrap';
import { 
  FaSearch, 
  FaUserPlus, 
  FaEllipsisV, 
  FaCheckCircle, 
  FaTimesCircle, 
  FaEnvelope, 
  FaPhone, 
  FaUpload, 
  FaIdCard, 
  FaFileAlt, 
  FaUserEdit, 
  FaEye,
  FaFilter,
  FaTimes,
  FaExclamationTriangle,
  FaCheck,
  FaUserClock,
  FaLink,
  FaKey // Add this icon
} from 'react-icons/fa';
import { dsoApi } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import './Team.css';
import { format } from 'date-fns';
import Select from 'react-select';
import Pagination from '../../components/Pagination';
import Swal from 'sweetalert2';

function DSOTeam() {
  const navigate = useNavigate();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [totalAgents, setTotalAgents] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectAll, setSelectAll] = useState(false);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false); // Add this line
  const [showFilters, setShowFilters] = useState(false);
  const pageSize = 10;

  // Filter states
  const [statusFilter, setStatusFilter] = useState(null);
  const [kycFilter, setKycFilter] = useState(null);
  const [sortBy, setSortBy] = useState({ value: 'recent', label: 'Most Recent' });
  const [filterApplied, setFilterApplied] = useState(false);

  // Filter options
  const statusOptions = [
    { value: 'active', label: 'Active' },
    { value: 'inactive', label: 'Inactive' }
  ];

  const kycOptions = [
    { value: 'complete', label: 'Complete' },
    { value: 'incomplete', label: 'Incomplete' },
    { value: 'not_submitted', label: 'Not Submitted' }
  ];

  const sortOptions = [
    { value: 'recent', label: 'Most Recent' },
    { value: 'name_asc', label: 'Name (A-Z)' },
    { value: 'name_desc', label: 'Name (Z-A)' },
    { value: 'profile_completion_high', label: 'Highest Completion' },
    { value: 'profile_completion_low', label: 'Lowest Completion' }
  ];

  useEffect(() => {
    fetchAgents();
  }, [currentPage]);

  const fetchAgents = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await dsoApi.getMyAgents({
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        search: searchTerm
      });
      
      console.log('Agents fetched:', response.data);
      
      // Apply client-side filtering
      let filteredAgents = response.data.items || [];
      
      // Filter by status if selected
      if (statusFilter) {
        filteredAgents = filteredAgents.filter(agent => {
          return statusFilter.value === 'active' ? agent.is_active : !agent.is_active;
        });
      }
      
      // Filter by KYC status if selected
      if (kycFilter) {
        filteredAgents = filteredAgents.filter(agent => {
          const kycStatus = getKycStatus(agent);
          return kycStatus === kycFilter.value;
        });
      }
      
      // Apply sorting
      if (sortBy) {
        filteredAgents = sortAgents(filteredAgents, sortBy.value);
      }
      
      setAgents(filteredAgents);
      setTotalAgents(response.data.total || 0);
      
      // Set filter applied flag
      setFilterApplied(!!statusFilter || !!kycFilter || sortBy.value !== 'recent');
      
      // Clear selection when fetching new agents
      setSelectedAgents([]);
      setSelectAll(false);
    } catch (err) {
      console.error('Error fetching agents:', err);
      setError('Failed to load team members. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Sorting function
  const sortAgents = (agents, sortValue) => {
    switch (sortValue) {
      case 'name_asc':
        return [...agents].sort((a, b) => a.name.localeCompare(b.name));
      case 'name_desc':
        return [...agents].sort((a, b) => b.name.localeCompare(a.name));
      case 'profile_completion_high':
        return [...agents].sort((a, b) => b.profile_completion - a.profile_completion);
      case 'profile_completion_low':
        return [...agents].sort((a, b) => a.profile_completion - b.profile_completion);
      case 'recent':
      default:
        return [...agents].sort((a, b) => {
          return new Date(b.created_at) - new Date(a.created_at);
        });
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchAgents();
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchAgents();
  };

  const clearFilters = () => {
    setStatusFilter(null);
    setKycFilter(null);
    setSortBy({ value: 'recent', label: 'Most Recent' });
    setCurrentPage(1);
    fetchAgents();
  };

  const handleStatusToggle = async (agentId, currentStatus) => {
    setActionLoading(agentId);
    try {
      await dsoApi.toggleAgentStatus(agentId, !currentStatus);
      // Update the agent in the list
      setAgents(agents.map(agent => {
        if (agent.id === agentId) {
          return { ...agent, is_active: !currentStatus };
        }
        return agent;
      }));

      // --- Add SweetAlert on success ---
      const agentName = agents.find(a => a.id === agentId)?.name || `Agent ID ${agentId}`;
      const actionText = currentStatus ? 'deactivated' : 'activated';
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: `Agent ${agentName} has been successfully ${actionText}.`,
        timer: 2000, // Auto close after 2 seconds
        showConfirmButton: false
      });
      // --- End SweetAlert ---

    } catch (err) {
      console.error('Error toggling agent status:', err);
      setError('Failed to update agent status. Please try again.');
      // --- Optional: Show error SweetAlert ---
      const errorMsg = err.response?.data?.detail || 'Failed to update agent status.';
      Swal.fire({
        icon: 'error',
        title: 'Error!',
        text: errorMsg,
      });
      // --- End Optional Error SweetAlert ---
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkStatusToggle = async (activate) => {
    if (selectedAgents.length === 0) return;
    
    setBulkActionLoading(true);
    try {
      // Process each selected agent
      for (const agentId of selectedAgents) {
        const agent = agents.find(a => a.id === agentId);
        if (agent && agent.is_active !== activate) {
          await dsoApi.toggleAgentStatus(agentId, activate);
        }
      }
      
      // Update the agents in the list
      setAgents(agents.map(agent => {
        if (selectedAgents.includes(agent.id)) {
          return { ...agent, is_active: activate };
        }
        return agent;
      }));
      
      // Clear selection
      setSelectedAgents([]);
      setSelectAll(false);

      // --- Add SweetAlert on success ---
      const actionText = activate ? 'activated' : 'deactivated';
      const count = selectedAgents.length;
      Swal.fire({
        icon: 'success',
        title: 'Bulk Action Success!',
        text: `${count} agent${count > 1 ? 's' : ''} successfully ${actionText}.`,
        timer: 2000, // Auto close after 2 seconds
        showConfirmButton: false
      });
      // --- End SweetAlert ---

    } catch (err) {
      console.error('Error toggling agent status in bulk:', err);
      setError('Failed to update agent status. Please try again.');
      // --- Optional: Show error SweetAlert ---
      const errorMsg = err.response?.data?.detail || 'Failed to update agent statuses in bulk.';
      Swal.fire({
        icon: 'error',
        title: 'Bulk Action Error!',
        text: errorMsg,
      });
      // --- End Optional Error SweetAlert ---
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleAgentSelection = (agentId) => {
    if (selectedAgents.includes(agentId)) {
      setSelectedAgents(selectedAgents.filter(id => id !== agentId));
    } else {
      setSelectedAgents([...selectedAgents, agentId]);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedAgents([]);
    } else {
      setSelectedAgents(agents.map(agent => agent.id));
    }
    setSelectAll(!selectAll);
  };

  const handleAssignLeads = (agent) => {
    navigate(`/dso/leads?agent_id=${agent.id}`);
  };

  const handleViewAgent = (agent) => {
    navigate(`/dso/agents/${agent.id}`);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const openActionModal = (agent) => {
    setSelectedAgent(agent);
    setShowActionModal(true);
  };

  const closeActionModal = () => {
    setShowActionModal(false);
    setSelectedAgent(null);
  };

  // Reset agent password function
  const handleResetPassword = async (agentId, agentName) => {
    try {
      closeActionModal(); // Close modal first before showing confirmation
      
      // Wait a moment to ensure the modal is closed
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Show confirmation dialog
      const confirmation = await Swal.fire({
        title: 'Reset Agent Password',
        text: `Are you sure you want to reset the password for agent "${agentName}"? They will need to be informed of their new temporary password.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, reset it!'
      });

      if (!confirmation.isConfirmed) {
        return; // User canceled
      }
      
      // Set loading state
      setResetPasswordLoading(true);
      setError('');

      // Call the API endpoint
      const response = await dsoApi.resetAgentPassword(agentId);
      
      // Get the temporary password from the response
      const tempPassword = response.data?.temp_password || '';

      // Show success message with copy functionality
      await Swal.fire({
        icon: 'success',
        title: 'Password Reset!',
        html: `
          <div>
            <p>Password for ${agentName} has been reset.</p>
            <p class="mb-2">Please share this temporary password with the agent:</p>
            <div class="password-container" style="display: flex; margin: 10px auto; max-width: 280px;">
              <input id="tempPasswordField" value="${tempPassword}" readonly 
                style="flex-grow: 1; padding: 8px; border: 1px solid #ccc; border-radius: 4px 0 0 4px; font-size: 16px;" />
              <button id="copyPasswordBtn" type="button" 
                style="padding: 8px; background: #3085d6; color: white; border: none; border-radius: 0 4px 4px 0; cursor: pointer;">
                Copy
              </button>
            </div>
          </div>
        `,
        confirmButtonText: 'Done',
        confirmButtonColor: '#3085d6',
        didOpen: () => {
          // Add click event to the copy button
          document.getElementById('copyPasswordBtn').addEventListener('click', () => {
            const passwordField = document.getElementById('tempPasswordField');
            passwordField.select();
            document.execCommand('copy');
            Swal.showValidationMessage('Password copied to clipboard!');
            setTimeout(() => {
              Swal.resetValidationMessage();
            }, 1500);
          });
        }
      });

    } catch (err) {
      console.error('Error resetting agent password:', err);
      const errorMsg = err.response?.data?.detail || 'Failed to reset agent password.';
      setError(errorMsg);

      // Show error message
      await Swal.fire({
        icon: 'error',
        title: 'Reset Failed!',
        text: errorMsg,
      });
    } finally {
      setResetPasswordLoading(false); // Stop loading indicator
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return format(new Date(dateString), 'dd MMM, yyyy');
  };

  const getKycStatus = (agent) => {
    if (!agent.has_pan && !agent.has_aadhaar) {
      return 'not_submitted';
    } else if (agent.has_pan && agent.has_aadhaar) {
      return 'complete';
    } else {
      return 'incomplete';
    }
  };

  const getKycBadge = (agent) => {
    const kycStatus = getKycStatus(agent);
    
    switch (kycStatus) {
      case 'complete':
        return <span className="team-status-badge status-success">Complete</span>;
      case 'incomplete':
        return <span className="team-status-badge status-warning">Incomplete</span>;
      case 'not_submitted':
      default:
        return <span className="team-status-badge status-danger">Not Submitted</span>;
    }
  };

  const renderAgentCompletionStatus = (percentage) => {
    let statusClass = '';
    let statusIcon = null;
    
    if (percentage < 40) {
      statusClass = 'text-danger';
      statusIcon = <span className="completion-icon low"><FaExclamationTriangle /></span>;
    } else if (percentage < 75) {
      statusClass = 'text-warning';
      statusIcon = <span className="completion-icon medium"><FaUserClock /></span>;
    } else {
      statusClass = 'text-success';
      statusIcon = <span className="completion-icon high"><FaCheckCircle /></span>;
    }
    
    return (
      <div className="agent-completion-status">
        {statusIcon}
        <div className={`completion-text ${statusClass}`}>
          <span className="percentage">{percentage}%</span>
          <span className="label">Complete</span>
        </div>
      </div>
    );
  };

  // Custom styles for react-select
  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: '36px',
      height: '36px',
      boxShadow: 'none',
      border: '1px solid #e2e8f0',
      borderRadius: '4px',
      fontSize: '0.85rem',
      '&:hover': {
        border: '1px solid #cbd5e1',
      },
    }),
    valueContainer: (provided) => ({
      ...provided,
      height: '36px',
      padding: '0 8px',
      display: 'flex',
      alignItems: 'center',
    }),
    input: (provided) => ({
      ...provided,
      margin: '0',
      padding: '0',
    }),
    indicatorsContainer: (provided) => ({
      ...provided,
      height: '36px',
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    dropdownIndicator: (provided) => ({
      ...provided,
      padding: '4px',
    }),
    clearIndicator: (provided) => ({
      ...provided,
      padding: '4px',
    }),
    option: (provided) => ({
      ...provided,
      fontSize: '0.85rem',
      padding: '6px 12px',
    }),
    placeholder: (provided) => ({
      ...provided,
      fontSize: '0.85rem',
      color: '#94a3b8',
    }),
    singleValue: (provided) => ({
      ...provided,
      fontSize: '0.85rem',
    }),
  };

  // Total pages calculation
  const totalPages = Math.ceil(totalAgents / pageSize);

  return (
    <div className="team-page">
      <div className="team-header">
        <h2>My Team</h2>
        <div className="team-actions">
          <Button 
            variant="outline-primary" 
            className="filter-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter /> Filters
          </Button>
          <Button variant="primary" onClick={() => navigate('/dso/agents/add')}>
            <FaUserPlus className="me-1" /> Add Agent
          </Button>
        </div>
      </div>

      {showFilters && (
        <div className="filter-section">
          <Form onSubmit={handleFilterSubmit}>
            <div className="filters-row">
              <div className="filter-item">
                <Form.Label>Search</Form.Label>
                <InputGroup>
                  <Form.Control
                    placeholder="Name, email or phone"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  <Button variant="primary" type="submit">
                    <FaSearch />
                  </Button>
                </InputGroup>
              </div>
              
              <div className="filter-item">
                <Form.Label>Status</Form.Label>
                <Select
                  value={statusFilter}
                  onChange={setStatusFilter}
                  options={statusOptions}
                  isClearable
                  placeholder="All Statuses"
                  styles={customSelectStyles}
                />
              </div>
              
              <div className="filter-item">
                <Form.Label>KYC Status</Form.Label>
                <Select
                  value={kycFilter}
                  onChange={setKycFilter}
                  options={kycOptions}
                  isClearable
                  placeholder="All KYC Statuses"
                  styles={customSelectStyles}
                />
              </div>
              
              <div className="filter-item">
                <Form.Label>Sort By</Form.Label>
                <Select
                  value={sortBy}
                  onChange={setSortBy}
                  options={sortOptions}
                  placeholder="Sort By"
                  styles={customSelectStyles}
                />
              </div>
              
              <div className="filter-actions">
                <Button 
                  variant="primary" 
                  type="submit" 
                  className="apply-filters-btn"
                >
                  Apply Filters
                </Button>
                
                {filterApplied && (
                  <Button 
                    variant="outline-secondary" 
                    onClick={clearFilters}
                    className="clear-filters-btn"
                  >
                    <FaTimes /> Clear
                  </Button>
                )}
              </div>
            </div>
          </Form>
        </div>
      )}
      
      {selectedAgents.length > 0 && (
        <div className="bulk-actions-bar">
          <span className="selected-count">
            {selectedAgents.length} {selectedAgents.length === 1 ? 'agent' : 'agents'} selected
          </span>
          <div className="bulk-action-buttons">
            <button
              className="bulk-action-button bulk-approve"
              onClick={() => handleBulkStatusToggle(true)}
              disabled={bulkActionLoading}
              title="Activate selected agents"
            >
              {bulkActionLoading ? <Spinner size="sm" /> : <><FaCheckCircle /> Bulk Activate</>}
            </button>
            <button
              className="bulk-action-button bulk-reject"
              onClick={() => handleBulkStatusToggle(false)}
              disabled={bulkActionLoading}
              title="Deactivate selected agents"
            >
              {bulkActionLoading ? <Spinner size="sm" /> : <><FaTimesCircle /> Bulk Deactivate</>}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="team-error-state">
          <FaExclamationTriangle className="team-error-icon" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="team-loading-state">
          <Spinner animation="border" role="status" className="team-spinner">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <span>Loading team members...</span>
        </div>
      ) : (
        <>
          {agents.length === 0 ? (
            <div className="team-no-data">No team members found.</div>
          ) : (
            <>
              <div className="team-table-container">
                <table className="team-table">
                  <thead>
                    <tr>
                      <th className="select-column">
                        <div className="select-header">
                          <input
                            type="checkbox"
                            className="select-checkbox"
                            checked={selectAll}
                            onChange={handleSelectAll}
                            title="Select all agents"
                          />
                        </div>
                      </th>
                      <th>Agent</th>
                      <th>Contact</th>
                      <th>KYC Status</th>
                      <th>Joined On</th>
                      <th>Status</th>
                      <th>Profile</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {agents.map((agent) => (
                      <tr key={agent.id} className={selectedAgents.includes(agent.id) ? 'selected-row' : ''}>
                        <td className="select-column">
                          <input
                            type="checkbox"
                            className="select-checkbox"
                            checked={selectedAgents.includes(agent.id)}
                            onChange={() => handleAgentSelection(agent.id)}
                            title="Select agent"
                          />
                        </td>
                        <td>
                          <div className="agent-info">
                            <div className="agent-avatar">
                              {agent.name ? agent.name.charAt(0).toUpperCase() : 'A'}
                            </div>
                            <div className="agent-details">
                              <div className="agent-name">{agent.name}</div>
                              <div className="agent-id">ID: AG{agent.id.toString().padStart(4, '0')}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="agent-contact">
                            <div className="agent-email" title="Email">
                              <FaEnvelope className="contact-icon" />
                              {agent.email}
                            </div>
                            <div className="agent-phone" title="Phone">
                              <FaPhone className="contact-icon" />
                              {agent.mobile || 'No phone'}
                            </div>
                          </div>
                        </td>
                        <td>{getKycBadge(agent)}</td>
                        <td>{formatDate(agent.created_at)}</td>
                        <td>
                          <span className={`team-status-badge ${agent.is_active ? 'status-success' : 'status-danger'}`}>
                            {agent.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          {renderAgentCompletionStatus(agent.profile_completion)}
                        </td>
                        <td>
                          <div className="team-action-buttons">
                            <button
                              className="team-icon-button button-view"
                              onClick={() => handleViewAgent(agent)}
                              title="View Agent"
                            >
                              <FaEye />
                            </button>
                            <button
                              className="team-icon-button button-leads"
                              onClick={() => handleAssignLeads(agent)}
                              title="Assign Leads"
                            >
                              <FaLink />
                            </button>
                            <button
                              className={`team-icon-button ${agent.is_active ? 'button-reject' : 'button-approve'}`}
                              onClick={() => handleStatusToggle(agent.id, agent.is_active)}
                              disabled={actionLoading === agent.id}
                              title={agent.is_active ? "Deactivate Agent" : "Activate Agent"}
                            >
                              {actionLoading === agent.id ? (
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                              ) : agent.is_active ? (
                                <FaTimes />
                              ) : (
                                <FaCheck />
                              )}
                            </button>
                            <button
                              className="team-icon-button button-more"
                              onClick={() => openActionModal(agent)}
                              title="More Actions"
                            >
                              <FaEllipsisV />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {totalPages > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  itemsCount={totalAgents}
                  itemsPerPage={pageSize}
                />
              )}
            </>
          )}
        </>
      )}

      {/* Action Modal */}
      <Modal show={showActionModal} onHide={closeActionModal} centered className="agent-action-modal">
        <Modal.Header closeButton>
          <Modal.Title>Agent Actions</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedAgent && (
            <div className="agent-modal-content">
              <div className="agent-modal-info">
                <div className="agent-avatar-large">
                  {selectedAgent.name ? selectedAgent.name.charAt(0).toUpperCase() : 'A'}
                </div>
                <div className="agent-details">
                  <h4>{selectedAgent.name}</h4>
                  <p className="text-muted mb-1">
                    <FaEnvelope className="me-2" /> {selectedAgent.email}
                  </p>
                  <p className="text-muted mb-2">
                    <FaPhone className="me-2" /> {selectedAgent.mobile || 'No phone number'}
                  </p>

                  <div className="agent-meta">
                    <div className="meta-item">
                      <span className="meta-label">KYC Status</span>
                      <span>{getKycBadge(selectedAgent)}</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Profile</span>
                      <span>{selectedAgent.profile_completion}% Complete</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Joined</span>
                      <span>{formatDate(selectedAgent.created_at)}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="agent-modal-actions">
                <button 
                  className="action-button button-primary"
                  onClick={() => {
                    closeActionModal();
                    handleViewAgent(selectedAgent);
                  }}
                >
                  <FaUserEdit /> Edit Details
                </button>
                <button 
                  className="action-button button-secondary"
                  onClick={() => {
                    closeActionModal();
                    handleAssignLeads(selectedAgent);
                  }}
                >
                  <FaFileAlt /> Assign Leads
                </button>
                <button
                  className="action-button button-warning"
                  onClick={() => handleResetPassword(selectedAgent.id, selectedAgent.name)} // Ensure this calls the updated function
                  disabled={resetPasswordLoading} // Ensure this uses the correct loading state
                >
                  {resetPasswordLoading ? (
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                  ) : (
                    <><FaKey /> Reset Password</> // Use FaKey icon
                  )}
                </button>
                <button
                  className={`action-button ${selectedAgent.is_active ? 'button-danger' : 'button-success'}`}
                  onClick={() => {
                    // Call handleStatusToggle directly, it will show the Swal alert
                    handleStatusToggle(selectedAgent.id, selectedAgent.is_active);
                    closeActionModal(); // Close modal immediately
                  }}
                  disabled={actionLoading === selectedAgent?.id} // Disable while loading
                >
                  {actionLoading === selectedAgent?.id ? (
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                  ) : selectedAgent.is_active ? (
                     <><FaTimesCircle /> Deactivate Agent</>
                   ) : (
                     <><FaCheckCircle /> Activate Agent</>
                   )}
                </button>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default DSOTeam;
